import React, { useState, useEffect, useMemo } from 'react';
import { Editor } from '@tiptap/react';
import AITextGenerationModal from './AITextGenerationModal';
import { usePageContentDetailQuery } from '@/hooks/content-generator/pages/use-page-content-hooks';
import { useUniversalResearchHooks } from '@/hooks/content-generator/research-attachments/use-universal-research-hooks';
import { ensureTipTapFormat } from '@/utils/ai-to-tiptap-converter';

interface InsertAITextDialogProps {
  editor: Editor;
  isOpen: boolean;
  onClose: () => void;
  workbookId?: string;
  pageId?: string;
}

export function InsertAITextDialog({ 
  editor, 
  isOpen, 
  onClose, 
  workbookId, 
  pageId 
}: InsertAITextDialogProps) {
  const [cursorPosition, setCursorPosition] = useState<{ from: number; to: number } | null>(null);
  const [selectedText, setSelectedText] = useState('');
  const [hasSelection, setHasSelection] = useState(false);

  // Get page content to access workbook_id and other page data
  const { data: pageContent } = usePageContentDetailQuery(pageId);
  
  // Get the actual workbook ID from page content (more reliable than prop)
  const actualWorkbookId = pageContent?.workbook_id || workbookId;
  
  // Fetch page research
  const pageResearchHooks = useUniversalResearchHooks('page');
  const { data: pageResearch = [] } = pageResearchHooks.useAttachedResearch(pageId);
  
  // Fetch workbook research - DISABLED (return empty array)
  const workbookResearch: any[] = [];

  // Combine and deduplicate research by research_id
  const combinedResearch = useMemo(() => {
    const allResearch = [...pageResearch, ...workbookResearch];
    const uniqueResearch = allResearch.filter((item, index, self) => 
      index === self.findIndex(r => r.research_id === item.research_id)
    );
    return uniqueResearch;
  }, [pageResearch, workbookResearch]);

  // Update cursor position and selected text when dialog opens
  useEffect(() => {
    if (isOpen && editor) {
      const { from, to } = editor.state.selection;
      setCursorPosition({ from, to });
      
      const selectedContent = editor.state.doc.textBetween(from, to, ' ');
      setSelectedText(selectedContent);
      setHasSelection(from !== to && selectedContent.trim().length > 0);
    }
  }, [isOpen, editor]);

  const handleTextGenerated = (generatedText: string, mode: 'insert' | 'replace' = 'insert') => {
    if (!editor || !generatedText.trim()) return;

    console.group('🎯 AI Text Generation Pipeline');
    console.log('📥 Raw AI Response:', {
      content: generatedText,
      length: generatedText.length,
      hasCodeFences: generatedText.includes('```'),
      preview: generatedText.substring(0, 100) + (generatedText.length > 100 ? '...' : '')
    });

    try {
      // Convert AI content to clean TipTap-compatible HTML
      let cleanContent = ensureTipTapFormat(generatedText);
      
      // Remove whitespace between HTML blocks to prevent TipTap from creating empty paragraphs
      cleanContent = cleanContent.replace(/>\s+</g, '><');
      
      console.log('🧹 Cleaned Content:', {
        content: cleanContent,
        length: cleanContent.length,
        wasModified: cleanContent !== generatedText,
        preview: cleanContent.substring(0, 100) + (cleanContent.length > 100 ? '...' : ''),
        whitespaceRemoved: true
      });

      console.log('📋 Modal Preview Display:', {
        displayedInModal: generatedText, // This is what shows in the modal preview
        note: 'Modal shows raw AI response for user preview'
      });

      console.log('💉 TipTap Injection:', {
        content: cleanContent,
        mode: mode,
        hasSelection: hasSelection,
        cursorPosition: cursorPosition
      });

      // Restore cursor position if available
      if (cursorPosition) {
        const docSize = editor.state.doc.content.size;
        if (cursorPosition.from <= docSize && cursorPosition.to <= docSize) {
          editor.commands.setTextSelection(cursorPosition);
        } else {
          editor.commands.focus('end');
        }
      } else {
        editor.commands.focus();
      }

      if (mode === 'replace' && hasSelection) {
        // Replace selected text while preserving surrounding formatting
        // Use TipTap's deleteSelection + insertContent to replace selected content
        editor.commands.deleteSelection();
        editor.commands.insertContent(cleanContent);
        
        console.log('✅ Content replaced using deleteSelection + insertContent - formatting preserved');
      } else {
        // Insert at cursor position while preserving existing content
        const currentHTML = editor.getHTML();
        
        // If editor is empty, just set the content directly
        if (!currentHTML || currentHTML.trim() === '<p></p>' || currentHTML.trim() === '') {
          editor.commands.setContent(cleanContent, false);
          console.log('✅ Content set in empty editor using setContent');
        } else {
          // Use insertContent to insert AI content at cursor position without affecting other content
          editor.commands.insertContent(cleanContent);
          console.log('✅ Content inserted using insertContent - formatting preserved');
        }
      }

      console.log('🎉 Pipeline Complete - Content Successfully Injected');
      console.groupEnd();

      // Close the modal
      onClose();
    } catch (error) {
      console.error('❌ Pipeline Error:', error);
      console.groupEnd();
      // Fall back to just focusing the editor
      editor.commands.focus();
    }
  };

  const handleClose = () => {
    setCursorPosition(null);
    setSelectedText('');
    setHasSelection(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AITextGenerationModal
      isOpen={isOpen}
      onClose={handleClose}
      onTextGenerated={handleTextGenerated}
      selectedText={selectedText}
      hasSelection={hasSelection}
      workbookId={actualWorkbookId}
      pageId={pageId}
      currentWritingStyle={pageContent?.writing_style || ''}
      pageName={pageContent?.title || ''}
      threadId={pageContent?.openai_thread_id || ''}
      pageResearch={pageResearch}
      workbookResearch={workbookResearch}
      researchSummaries={combinedResearch}
    />
  );
} 